/**
 * Listas de precios.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "El proveedor me mandó la lista nueva: ¿qué me cambia?" — y recién después,
 * "dale, aplicala".
 *
 * LO QUE HACE PELIGROSA A ESTA PANTALLA
 * -------------------------------------
 * Es el único lugar del sistema donde un click reescribe cientos de costos de
 * una. Y el costo no queda ahí: `trg_productos_precio` recalcula el precio de
 * venta de cada producto tocado, con su markup y su redondeo. Un import mal
 * aplicado no rompe una fila: repricia el catálogo.
 *
 * Todo el diseño gira alrededor de eso, en cuatro reglas que no se negocian:
 *
 *   1. NADA se escribe sin que un humano lo mire. El backend propone; la
 *      pantalla nunca "arregla" la propuesta. Los matches por nombre vienen en
 *      IGNORAR porque nadie verificó que sean el mismo producto, y ahí se
 *      quedan hasta que alguien decida.
 *   2. El orden de la revisión es por peligro, no por número de fila. Primero lo
 *      que cambia de moneda, después las variaciones enormes (un +80% suele ser
 *      un punto decimal mal leído del PDF), después los matches flojos.
 *   3. El impacto se ve ANTES: cuántos productos, cuánto sube el costo por
 *      moneda, qué precio de venta queda.
 *   4. El botón de revertir está a la vista desde el segundo en que se aplica.
 *      Una red de seguridad que hay que buscar no es una red.
 *
 * SOBRE LOS HOOKS
 * ---------------
 * `lib/queries.ts` no tiene hooks de listas y no es de este trabajo tocarlo:
 * viven en `components/listas/consultas.ts`, contra `api` de `lib/api.ts`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileUp, Tags, Undo2 } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Segmented } from "@/components/ui/segmented";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { AvisoMoneda } from "@/components/listas/AvisoMoneda";
import { BarraAcciones } from "@/components/listas/BarraAcciones";
import { DialogAplicar, DialogRevertir, PanelAplicado } from "@/components/listas/DialogsAplicar";
import { DialogProducto } from "@/components/listas/DialogProducto";
import { ListasLeidas } from "@/components/listas/ListasLeidas";
import { AvisoComprobante, ListaEnUnaPantalla } from "@/components/listas/ListaEnUnaPantalla";
import { useSubirCompra } from "@/components/compras/consultas";
import { palabrasDeBusqueda } from "@/components/compras/logica";
import { PanelImpacto, BarraAplicar } from "@/components/listas/PanelImpacto";
import { ProcesoImport } from "@/components/listas/ProcesoImport";
import { SubidaLista } from "@/components/listas/SubidaLista";
import { TablaRevision } from "@/components/listas/TablaRevision";
import {
  type CambiosFila as CambiosFilaLista,
  importRecordado,
  recordarImport,
  useActualizarFila,
  useActualizarFilas,
  useAplicarImport,
  useCambiarProveedorLista,
  useImportLista,
  useImportsRecientes,
  useRevertirImport,
  useRevisionLista,
  useSubirLista,
} from "@/components/listas/consultas";
import {
  alertaDeMoneda,
  calcularImpacto,
  resumenDeAplicado,
  esSospechosa,
  filtrarFilas,
  ordenarComoElArchivo,
  ordenarParaRevisar,
  sePuedeAplicar,
  type AccionFila,
  type FilaRevision,
  type ResultadoAplicar,
} from "@/components/listas/logica";
import { useConfig, useProveedores } from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { aNumero } from "@/lib/tipos";

export const Route = createFileRoute("/listas-precios")({ component: ListasPrecios });

type FiltroAccion = AccionFila | "TODAS";
type Orden = "revisar" | "archivo";

const FILTROS: ReadonlyArray<{ valor: FiltroAccion; etiqueta: string }> = [
  { valor: "TODAS", etiqueta: "Todas" },
  { valor: "ACTUALIZAR", etiqueta: "Se actualizan" },
  { valor: "CREAR", etiqueta: "Se crean" },
  { valor: "IGNORAR", etiqueta: "Quedan igual" },
];

const ORDENES: ReadonlyArray<{ valor: Orden; etiqueta: string }> = [
  { valor: "revisar", etiqueta: "Para revisar" },
  { valor: "archivo", etiqueta: "Orden del archivo" },
];

function ListasPrecios(): JSX.Element {
  // El id arranca de lo guardado en el navegador: sin listado en el backend, un
  // F5 despues de aplicar dejaria el boton de revertir inalcanzable.
  const [importId, setImportId] = useState<string | null>(() => importRecordado());
  const [archivoNombre, setArchivoNombre] = useState<string | null>(null);
  const [progresoSubida, setProgresoSubida] = useState(0);
  // La fila lleva el lapiz y el segmented de accion (32 px): normal por default.

  const [filtro, setFiltro] = useState<FiltroAccion>("TODAS");
  const [soloSospechosas, setSoloSospechosas] = useState(false);
  const [texto, setTexto] = useState("");
  const [orden, setOrden] = useState<Orden>("revisar");

  const [filaEnEdicion, setFilaEnEdicion] = useState<FilaRevision | null>(null);
  const [abrirAplicar, setAbrirAplicar] = useState(false);
  const [abrirRevertir, setAbrirRevertir] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState<ResultadoAplicar | null>(null);

  useEffect(() => {
    recordarImport(importId);
  }, [importId]);

  const { data: proveedores } = useProveedores();
  const { data: config } = useConfig();
  const cotizacion = aNumero(config?.cotizacion_vigente);

  const subir = useSubirLista();
  const { data: recientes } = useImportsRecientes();
  const { data: datosImport, isError: importFallo } = useImportLista(importId);
  const imp = datosImport?.import ?? null;

  // La revision se pide solo cuando hay filas: mientras la IA lee, la vista
  // devolveria una lista vacia que parece un import sin resultados.
  const conFilas = imp?.estado === "COMPLETADO" || imp?.estado === "APLICADO";
  const { data: datosRevision, isFetching: cargandoRevision } = useRevisionLista(
    importId,
    conFilas,
  );
  const filas = useMemo(() => datosRevision?.filas ?? [], [datosRevision]);

  const actualizarFila = useActualizarFila(importId);
  const actualizarFilas = useActualizarFilas(importId);
  const cambiarProveedor = useCambiarProveedorLista(importId);
  const subirCompra = useSubirCompra();
  const navigate = useNavigate();
  // El archivo que se subio en esta visita: si resulta ser una compra, se
  // manda a Compras sin pedirlo de nuevo.
  const archivoSubido = useRef<File | null>(null);
  // «No, es una lista de precios», atado a la lista.
  const [esListaIgual, setEsListaIgual] = useState<string | null>(null);
  const aplicar = useAplicarImport(importId);
  const revertir = useRevertirImport(importId);

  const proveedor = useMemo(
    () => (proveedores?.filas ?? []).find((p) => p.id === imp?.proveedor_id) ?? null,
    [proveedores, imp?.proveedor_id],
  );

  const alerta = useMemo(
    () => (imp === null ? null : alertaDeMoneda(imp, proveedor, filas)),
    [imp, proveedor, filas],
  );

  const impacto = useMemo(() => calcularImpacto(filas), [filas]);

  const visibles = useMemo(() => {
    const filtradas = filtrarFilas(filas, { accion: filtro, soloSospechosas, texto });
    return orden === "revisar" ? ordenarParaRevisar(filtradas) : ordenarComoElArchivo(filtradas);
  }, [filas, filtro, soloSospechosas, texto, orden]);

  // Para el diálogo: lo que más se mueve, entre lo que de verdad se va a aplicar.
  const sospechosas = useMemo(
    () => ordenarParaRevisar(filas.filter((f) => f.accion !== "IGNORAR" && esSospechosa(f))),
    [filas],
  );

  const aplicado = imp?.estado === "APLICADO";
  const puedeAplicar = sePuedeAplicar(imp, impacto);
  const hayFiltros = filtro !== "TODAS" || soloSospechosas || texto !== "";

  function limpiarFiltros(): void {
    setFiltro("TODAS");
    setSoloSospechosas(false);
    setTexto("");
  }

  function empezarDeNuevo(): void {
    setImportId(null);
    setArchivoNombre(null);
    setProgresoSubida(0);
    setUltimoResultado(null);
    setFiltro("TODAS");
    setSoloSospechosas(false);
    setTexto("");
    subir.reset();
  }

  function subirArchivo(archivo: File, proveedorId: number | null): void {
    archivoSubido.current = archivo;
    setArchivoNombre(archivo.name);
    setProgresoSubida(0);
    setUltimoResultado(null);
    subir.mutate(
      { archivo, proveedorId, onProgreso: setProgresoSubida },
      {
        onSuccess: (respuesta) => setImportId(respuesta.import_id),
        onError: (error) => {
          setArchivoNombre(null);
          mostrarError(error);
        },
      },
    );
  }

  function cambiarAccion(filaId: number, accion: AccionFila): void {
    actualizarFila.mutate({ filaId, cambios: { accion } }, { onError: mostrarError });
  }

  function elegirProducto(productoId: number | null): void {
    const fila = filaEnEdicion;
    if (fila === null) return;
    // Elegir el producto ya es decidir: queda para actualizar (0150). Antes
    // quedaba «Ignorar» y habia que tocar otra vez la fila.
    actualizarFila.mutate(
      {
        filaId: fila.id,
        cambios:
          productoId === null
            ? { producto_id: null, accion: "IGNORAR" }
            : { producto_id: productoId, accion: "ACTUALIZAR" },
      },
      {
        onSuccess: () => {
          setFilaEnEdicion(null);
          mostrarExito(
            productoId === null ? "Fila desasociada" : "Producto asignado a mano",
            productoId === null
              ? "Vuelve a quedar sin producto, así que no se puede actualizar."
              : "La próxima lista de este proveedor lo reconoce sola.",
          );
        },
        onError: mostrarError,
      },
    );
  }

  function confirmarAplicar(): void {
    aplicar.mutate(undefined, {
      onSuccess: (resultado) => {
        setAbrirAplicar(false);
        setUltimoResultado(resultado);
        mostrarExito(
          `Se aplicó la lista: ${resumenDeAplicado(impacto, resultado.creados)}`,
          "Si algo quedó mal, el botón de revertir está arriba de la tabla.",
        );
      },
      onError: mostrarError,
    });
  }

  function confirmarRevertir(): void {
    revertir.mutate(undefined, {
      onSuccess: (resultado) => {
        setAbrirRevertir(false);
        setUltimoResultado(null);
        mostrarExito(
          `Se revirtieron ${resultado.productos} productos`,
          "Cada costo volvió a lo que valía y los precios se recalcularon.",
        );
      },
      onError: mostrarError,
    });
  }

  async function decidirFila(filaId: number, cambios: CambiosFilaLista): Promise<void> {
    try {
      await actualizarFila.mutateAsync({ filaId, cambios });
    } catch (error) {
      mostrarError(error);
    }
  }

  async function decidirFilas(cambios: Array<{ id: number } & CambiosFilaLista>): Promise<void> {
    try {
      await actualizarFilas.mutateAsync(cambios);
      mostrarExito("Listo", "La próxima lista de este proveedor los reconoce solos.");
    } catch (error) {
      mostrarError(error);
    }
  }

  function aplicarDirecto(): void {
    aplicar.mutate(undefined, {
      onSuccess: (resultado) => {
        setUltimoResultado(resultado);
        mostrarExito(
          `Se actualizaron ${resultado.actualizados} ${resultado.actualizados === 1 ? "costo" : "costos"}`,
          "Ningún precio bajó. Si algo quedó mal, Revertir está arriba.",
        );
      },
      onError: mostrarError,
    });
  }

  function pasarACompras(): void {
    const archivo = archivoSubido.current;
    if (archivo === null) {
      // Abierta desde otra visita: el archivo ya no esta en el navegador.
      mostrarExito("Subila en Compras", "Es una compra: ahí suma el stock y la deuda.");
      void navigate({ to: "/compras", search: { nuevo: true } });
      return;
    }
    subirCompra.mutate(
      { archivo },
      {
        onSuccess: (respuesta) => {
          mostrarExito("La pasamos a Compras", "Se está leyendo ahí. Acá no se tocó nada.");
          void navigate({ to: "/compras", search: { id: respuesta.compra_id } });
        },
        onError: mostrarError,
      },
    );
  }

  const mostrandoSubida = importId === null && !subir.isPending;

  return (
    <Pagina>
      <PageHeader
        titulo="Listas de precios"
        icono={Tags}
        acciones={
          importId !== null ? (
            <Button type="button" variant="outline" onClick={empezarDeNuevo}>
              <FileUp aria-hidden="true" />
              Otra lista
            </Button>
          ) : null
        }
      />

      {mostrandoSubida ? (
        <SubidaLista subiendo={subir.isPending} progreso={progresoSubida} onSubir={subirArchivo} />
      ) : null}

      {mostrandoSubida ? (
        <ListasLeidas
          imports={recientes ?? []}
          proveedores={proveedores?.filas ?? []}
          onAbrir={setImportId}
        />
      ) : null}

      {importFallo && importId !== null ? <NoSePudoLeer onEmpezarDeNuevo={empezarDeNuevo} /> : null}

      {!mostrandoSubida && !conFilas && !importFallo ? (
        <ProcesoImport
          imp={imp}
          archivoNombre={archivoNombre}
          subiendo={subir.isPending}
          progresoSubida={progresoSubida}
          onEmpezarDeNuevo={empezarDeNuevo}
        />
      ) : null}

      {conFilas && imp !== null && !aplicado ? (
        imp.tipo_documento === "COMPROBANTE" && esListaIgual !== imp.id ? (
          <AvisoComprobante
            imp={imp}
            pasando={subirCompra.isPending}
            onPasarACompras={pasarACompras}
            onEsUnaLista={() => setEsListaIgual(imp.id)}
          />
        ) : (
          <ListaEnUnaPantalla
            imp={imp}
            filas={filas}
            proveedores={proveedores?.filas ?? []}
            // Sin proveedor, la pregunta de arriba ya lo dice: el aviso viejo
            // («el import no tiene proveedor») lo repetia.
            avisoMoneda={imp.proveedor_id === null ? null : <AvisoMoneda alerta={alerta} />}
            guardando={
              actualizarFila.isPending || actualizarFilas.isPending || cambiarProveedor.isPending
            }
            aplicando={aplicar.isPending}
            onCambiarProveedor={(id) => cambiarProveedor.mutate(id, { onError: mostrarError })}
            onActualizarFila={decidirFila}
            onActualizarFilas={decidirFilas}
            onBuscarProducto={setFilaEnEdicion}
            onAplicar={aplicarDirecto}
          />
        )
      ) : null}

      {/* Aplicada: lo que entro y Revertir, como siempre. */}
      {conFilas && imp !== null && aplicado ? (
        <>
          {aplicado ? (
            <PanelAplicado
              imp={imp}
              resultado={ultimoResultado}
              impacto={impacto}
              revirtiendo={revertir.isPending}
              onRevertir={() => setAbrirRevertir(true)}
            />
          ) : null}

          <AvisoMoneda alerta={alerta} />

          {/* La columna del panel recien desde 2xl: al lado de la tabla desde
              xl, a 1400 px la tabla quedaba en 802 px y «Al aplicar» —la
              columna que se toca— detras del scroll horizontal. Mas angosto,
              el panel va arriba y la tabla usa el ancho entero. */}
          <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
            <section className="space-y-3 2xl:order-first">
              <Filtros
                filtro={filtro}
                onFiltro={setFiltro}
                soloSospechosas={soloSospechosas}
                onSoloSospechosas={setSoloSospechosas}
                texto={texto}
                onTexto={setTexto}
                orden={orden}
                onOrden={setOrden}
                visibles={visibles.length}
                total={filas.length}
                sospechosas={impacto.sospechosas}
              />

              <TablaRevision
                filas={visibles}
                bloqueada={aplicado}
                guardandoId={
                  actualizarFila.isPending ? (actualizarFila.variables?.filaId ?? null) : null
                }
                onAccion={cambiarAccion}
                onElegirProducto={setFilaEnEdicion}
                cargando={cargandoRevision && filas.length === 0}
                mensajeVacio={
                  filas.length === 0
                    ? "La lista no trajo ninguna fila."
                    : "Ninguna fila entra en este filtro."
                }
                accionVacio={
                  hayFiltros ? (
                    <Button type="button" variant="outline" size="sm" onClick={limpiarFiltros}>
                      Limpiar filtros
                    </Button>
                  ) : undefined
                }
              />
            </section>

            {/*
              En mobile el panel va PRIMERO: es el resumen con el que se decide,
              y ponerlo despues de 400 filas equivale a esconderlo. En escritorio
              vuelve a la columna derecha y queda pegado arriba.
            */}
            <div className="order-first 2xl:sticky 2xl:top-6 2xl:order-last 2xl:self-start">
              <PanelImpacto
                impacto={impacto}
                cotizacion={cotizacion}
                aplicado={aplicado}
                accion={
                  // Aplicado, el boton de revertir ya esta arriba en su propio
                  // panel y abajo en la barra: repetirlo una tercera vez es ruido.
                  aplicado ? undefined : (
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => setAbrirAplicar(true)}
                      disabled={!puedeAplicar}
                      cargando={aplicar.isPending}
                    >
                      Aplicar la lista
                    </Button>
                  )
                }
              />
            </div>
          </div>

          {!aplicado ? (
            <BarraAplicar
              impacto={impacto}
              puedeAplicar={puedeAplicar}
              aplicando={aplicar.isPending}
              onAplicar={() => setAbrirAplicar(true)}
            />
          ) : (
            <BarraAcciones
              sangria="pagina"
              resumen={
                <>
                  {/* El estado de la LISTA como badge (MASTER §10), no como parrafo. */}
                  <Badge variant="success">Aplicada</Badge>
                  <p className="text-muted-foreground">Los precios de venta se recalcularon.</p>
                </>
              }
            >
              <Button
                type="button"
                variant="outline"
                onClick={() => setAbrirRevertir(true)}
                cargando={revertir.isPending}
              >
                <Undo2 aria-hidden="true" />
                Revertir todo
              </Button>
            </BarraAcciones>
          )}
        </>
      ) : null}

      <DialogProducto
        fila={filaEnEdicion}
        abierto={filaEnEdicion !== null}
        onAbiertoChange={(a) => {
          if (!a) setFilaEnEdicion(null);
        }}
        proveedorId={imp?.proveedor_id ?? null}
        guardando={actualizarFila.isPending}
        onElegir={elegirProducto}
        textoInicial={filaEnEdicion ? palabrasDeBusqueda(filaEnEdicion.descripcion) : ""}
        incluirDadosDeBaja
        soloDelProveedorAlAbrir={false}
        descripcion={
          filaEnEdicion
            ? `La lista dice «${filaEnEdicion.descripcion}». Elegí cuál de tus productos es: queda aprendido para la próxima lista.`
            : ""
        }
      />

      <DialogAplicar
        abierto={abrirAplicar}
        onAbiertoChange={setAbrirAplicar}
        impacto={impacto}
        alerta={alerta}
        sospechosas={sospechosas}
        aplicando={aplicar.isPending}
        onConfirmar={confirmarAplicar}
      />

      <DialogRevertir
        abierto={abrirRevertir}
        onAbiertoChange={setAbrirRevertir}
        imp={imp}
        revirtiendo={revertir.isPending}
        onConfirmar={confirmarRevertir}
      />
    </Pagina>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtros
// ─────────────────────────────────────────────────────────────────────────────

interface FiltrosProps {
  filtro: FiltroAccion;
  onFiltro: (f: FiltroAccion) => void;
  soloSospechosas: boolean;
  onSoloSospechosas: (v: boolean) => void;
  texto: string;
  onTexto: (t: string) => void;
  orden: Orden;
  onOrden: (o: Orden) => void;
  visibles: number;
  total: number;
  sospechosas: number;
}

function Filtros({
  filtro,
  onFiltro,
  soloSospechosas,
  onSoloSospechosas,
  texto,
  onTexto,
  orden,
  onOrden,
  visibles,
  total,
  sospechosas,
}: FiltrosProps): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <InputBusqueda
          valor={texto}
          onValorChange={onTexto}
          etiqueta="Buscar fila"
          placeholder="Buscar en la lista o en el catálogo"
          className="min-w-0 flex-1"
        />

        {/*
          El orden por defecto es el de revision y no el del archivo. El del
          archivo solo sirve para cotejar contra el PDF; el de revision es el que
          pone arriba lo que puede costar plata.
        */}
        <Segmented
          apariencia="segmentado"
          tamano="sm"
          etiqueta="Orden de la lista"
          opciones={ORDENES}
          valor={orden}
          onChange={onOrden}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          apariencia="chips"
          tamano="sm"
          etiqueta="Filtrar por lo que va a pasar"
          opciones={FILTROS}
          valor={filtro}
          onChange={onFiltro}
        />
        {/*
          "Para mirar" no es una solapa mas: se prende y se apaga encima del
          filtro de accion. Un chip suelto con `aria-pressed`, del mismo alto
          que los del Segmented.
        */}
        <Button
          type="button"
          size="sm"
          variant={soloSospechosas ? "default" : "outline"}
          aria-pressed={soloSospechosas}
          onClick={() => onSoloSospechosas(!soloSospechosas)}
        >
          Para mirar ({sospechosas})
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {visibles} de {total} filas
        </p>
      </div>
    </div>
  );
}

function NoSePudoLeer({ onEmpezarDeNuevo }: { onEmpezarDeNuevo: () => void }): JSX.Element {
  return (
    <Card tono="destructive" padding="normal" className="space-y-3 sm:p-6">
      <TituloSeccion>No pudimos abrir ese import</TituloSeccion>
      <p className="text-sm text-muted-foreground">
        Puede que se haya borrado o que sea de otra instalación. No se tocó ningún precio.
      </p>
      <Button type="button" variant="outline" onClick={onEmpezarDeNuevo}>
        Subir otra lista
      </Button>
    </Card>
  );
}
